<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2">

      <b-card class="mb-0 auth-card">
        <!-- Language Selector -->
        <div class="locale-dropdown-wrapper">
          <b-dropdown
            variant="link"
            no-caret
            right
            toggle-class="p-0 text-decoration-none"
          >
            <template #button-content>
              <div class="locale-toggle-btn shadow-none">
                <b-img
                  :src="currentLocale.img"
                  height="14px"
                  width="20px"
                  :alt="currentLocale.locale"
                  class="mr-50 rounded-sm"
                />
                <span class="font-small-2 font-weight-bold text-body mr-25">{{ currentLocale.name }}</span>
                <feather-icon
                  icon="ChevronDownIcon"
                  size="14"
                  class="text-muted"
                />
              </div>
            </template>
            <b-dropdown-item
              v-for="localeObj in locales"
              :key="localeObj.locale"
              :active="localeObj.locale === $i18n.locale"
              @click="changeLocale(localeObj.locale)"
            >
              <b-img
                :src="localeObj.img"
                height="14px"
                width="20px"
                :alt="localeObj.locale"
                class="mr-50 rounded-sm"
              />
              <span class="font-small-2">{{ localeObj.name }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>

        <!-- Logo -->
        <div class="text-center mb-2">
          <b-link
            v-show="!loadingLogo"
            class="brand-logo d-inline-block"
          >
            <b-img
              fluid
              :src="resolveLogo()"
              alt="Logo"
              class="brand-logo-img"
            />
          </b-link>
          <div
            v-show="loadingLogo"
            style="height: 3rem;"
          />
        </div>

        <!-- Header / Security Badge -->
        <div class="text-center mb-2">
          <div class="access-key-badge mb-1 shadow-sm">
            <feather-icon
              icon="KeyIcon"
              size="26"
            />
          </div>
          <b-card-title class="font-weight-bolder mb-50">
            {{ $t('Access Key') }}
          </b-card-title>
          <b-card-text class="text-muted font-small-3 mb-0">
            {{ $t('Enter the 6-digit access key sent to') }}
          </b-card-text>

          <!-- Email chip -->
          <div
            v-if="userEmail && !isEditingEmail"
            class="email-chip-container mt-75"
          >
            <div class="email-chip">
              <feather-icon
                icon="MailIcon"
                size="14"
                class="text-primary mr-50 flex-shrink-0"
              />
              <span class="email-text text-truncate">{{ userEmail }}</span>
              <b-button
                v-b-tooltip.hover
                variant="link"
                size="sm"
                class="p-0 ml-50 text-muted btn-edit-email"
                :title="$t('Change email')"
                @click="toggleEditEmail"
              >
                <feather-icon
                  icon="Edit2Icon"
                  size="13"
                />
              </b-button>
            </div>
          </div>
        </div>

        <!-- Form -->
        <validation-observer
          ref="simpleRules"
          #default="{ invalid }"
        >
          <b-form
            class="auth-access-key-form"
            @submit.prevent="validationForm"
          >

            <!-- Email field (collapsible when editing or email missing) -->
            <b-collapse :visible="isEditingEmail || !userEmail">
              <b-form-group
                :label="$t('Email')"
                label-for="access-key-email"
                class="mb-1 text-left"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Email"
                  rules="required|email"
                >
                  <b-input-group
                    class="input-group-merge"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  >
                    <b-input-group-prepend is-text>
                      <feather-icon icon="MailIcon" />
                    </b-input-group-prepend>
                    <b-form-input
                      id="access-key-email"
                      v-model="userEmail"
                      type="email"
                      :state="errors.length > 0 ? false : null"
                      placeholder="usuario@empresa.com"
                      @keyup.enter="handleEmailEnter"
                    />
                  </b-input-group>
                  <div class="d-flex justify-content-between align-items-center mt-25">
                    <small class="text-danger">{{ errors[0] }}</small>
                    <b-button
                      v-if="previousEmail"
                      variant="link"
                      size="sm"
                      class="p-0 text-muted font-small-2 ml-auto"
                      @click="cancelEditEmail"
                    >
                      {{ $t('Cancel') }}
                    </b-button>
                  </div>
                </validation-provider>
              </b-form-group>
            </b-collapse>

            <!-- Access Key Input -->
            <b-form-group
              :label="$t('6-digit Code')"
              label-for="Acess-Key"
              class="text-center mb-1"
            >
              <validation-provider
                #default="{ errors }"
                name="Key"
                rules="required|length:6"
              >
                <div class="position-relative">
                  <cleave
                    id="Acess-Key"
                    ref="keyInput"
                    v-model="key"
                    class="form-control access-key-input"
                    :raw="false"
                    :options="keyMask"
                    placeholder="••••••"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    autofocus
                    @keyup.enter="validationForm"
                  />
                </div>
                <small
                  v-if="errors[0]"
                  class="text-danger d-block mt-50"
                >
                  {{ $t('Access key is required and must be 6 digits.') }}
                </small>
              </validation-provider>
            </b-form-group>

            <!-- Submit Button -->
            <b-button
              variant="primary"
              block
              type="submit"
              size="lg"
              class="font-weight-bolder mt-1 btn-submit"
              :disabled="invalid || loading || !key || key.length < 6 || !userEmail"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-50"
              />
              <span>{{ loading ? $t('Verifying...') : $t('Verify and Sign In') }}</span>
            </b-button>
          </b-form>
        </validation-observer>

        <!-- Resend Code -->
        <div class="text-center mt-2">
          <span class="text-muted font-small-3 mr-50">{{ $t("Didn't receive the code?") }}</span>
          <b-button
            v-if="countdown === 0"
            variant="link"
            class="p-0 font-small-3 font-weight-bolder text-primary align-baseline"
            :disabled="resending || !userEmail"
            @click="resendKey"
          >
            <b-spinner
              v-if="resending"
              small
              class="mr-25"
            />
            <span>{{ $t('Resend code') }}</span>
          </b-button>
          <span
            v-else
            class="text-primary font-small-3 font-weight-bolder"
          >
            {{ $t('Resend in {seconds}s', { seconds: countdown }) }}
          </span>
        </div>

        <div class="divider my-2">
          <div class="divider-text font-small-2 text-uppercase">
            {{ $t('or') }}
          </div>
        </div>

        <!-- Back to login link -->
        <b-card-text class="text-center mb-0">
          <b-link
            :to="{ name: 'auth-login' }"
            class="d-inline-flex align-items-center font-weight-bold"
          >
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
              class="mr-25"
            />
            <span>{{ $t('Back to login') }}</span>
          </b-link>
        </b-card-text>

      </b-card>

    </div>
  </div>
</template>

<script>
/* eslint-disable global-require */
/* eslint-disable import/no-cycle */
import {
  ValidationProvider,
  ValidationObserver,
} from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BCard,
  BLink,
  BCardText,
  BCardTitle,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BForm,
  BButton,
  BImg,
  BSpinner,
  BCollapse,
  BDropdown,
  BDropdownItem,
  VBTooltip,
} from 'bootstrap-vue'
import {
  required,
  email,
  length,
} from '@validations'

import Cleave from 'vue-cleave-component'
import axios from '@axios'
import useJwt from '@/auth/jwt/useJwt'
import customerSettigns from '@core/utils/customerSettigns'
import store from '@/store'
import systemStoreModule from '@core/layouts/components/app-navbar/systemStoreModule'
import safeSpotStoreModule from '@/views/main/safe-spot/safeSpotStoreModule'
import { setSafeSpotPermission } from '@/utils/safeSpotPermission'
import { onUnmounted } from '@vue/composition-api'

export default {
  components: {
    BCard,
    BLink,
    BCardText,
    BCardTitle,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BButton,
    BForm,
    BCollapse,
    BDropdown,
    BDropdownItem,
    BImg,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    Cleave,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      required,
      email,
      length,

      userEmail: '',
      previousEmail: '',
      isEditingEmail: false,
      key: '',
      loading: false,
      resending: false,
      countdown: 0,
      timer: null,

      keyMask: {
        blocks: [6],
        numericOnly: true,
      },

      userSettings: null,
      loadingLogo: true,

      previousAppLocale: null,
      isLoggedIn: false,

      locales: [
        {
          locale: 'pt',
          img: require('@/assets/images/flags/br.png'),
          name: 'Português',
        },
        {
          locale: 'en',
          img: require('@/assets/images/flags/en.png'),
          name: 'English',
        },
        {
          locale: 'es',
          img: require('@/assets/images/flags/ar.png'),
          name: 'Español',
        },
      ],
    }
  },
  computed: {
    currentLocale() {
      return this.locales.find(l => l.locale === this.$i18n.locale) || this.locales[0]
    },
  },
  watch: {
    key(val) {
      if (val && val.length === 6 && !this.loading && this.userEmail && !this.isEditingEmail) {
        this.validationForm()
      }
    },
  },
  created() {
    // Guarda o idioma anterior da aplicação e define 'pt' como padrão para esta página
    this.previousAppLocale = this.$i18n.locale
    this.$i18n.locale = 'pt'
  },
  mounted() {
    if (localStorage.userEmail) {
      this.userEmail = localStorage.userEmail
      this.previousEmail = localStorage.userEmail
      this.isEditingEmail = false
    } else {
      this.isEditingEmail = true
    }

    this.getSetings()

    this.$nextTick(() => {
      if (this.isEditingEmail) {
        const emailInput = document.getElementById('access-key-email')
        if (emailInput) emailInput.focus()
      } else {
        const keyInput = document.getElementById('Acess-Key')
        if (keyInput) keyInput.focus()
      }
    })
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }

    // Se o usuário não concluiu o login com sucesso nesta página, restaura o idioma anterior da aplicação
    if (!this.isLoggedIn && this.previousAppLocale) {
      this.$i18n.locale = this.previousAppLocale
    }
  },
  methods: {
    changeLocale(locale) {
      this.$i18n.locale = locale
    },
    async getSetings() {
      this.loadingLogo = true
      this.userSettings = await customerSettigns()
      this.loadingLogo = false
    },
    resolveLogo() {
      if (!this.userSettings) {
        return require('@/assets/images/logo/LogoExtend.png')
      }
      return this.userSettings.logoUrl
    },
    toggleEditEmail() {
      if (!this.isEditingEmail) {
        this.previousEmail = this.userEmail
        this.isEditingEmail = true
        this.$nextTick(() => {
          const emailInput = document.getElementById('access-key-email')
          if (emailInput) emailInput.focus()
        })
      } else {
        if (this.userEmail) {
          localStorage.userEmail = this.userEmail.trim()
          this.previousEmail = this.userEmail.trim()
        }
        this.isEditingEmail = false
      }
    },
    cancelEditEmail() {
      this.userEmail = this.previousEmail
      this.isEditingEmail = false
    },
    handleEmailEnter() {
      if (this.userEmail) {
        localStorage.userEmail = this.userEmail.trim()
        this.previousEmail = this.userEmail.trim()
        this.isEditingEmail = false
        this.$nextTick(() => {
          const keyInput = document.getElementById('Acess-Key')
          if (keyInput) keyInput.focus()
        })
      }
    },
    startCountdown(seconds = 60) {
      this.countdown = seconds
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown -= 1
        } else {
          clearInterval(this.timer)
          this.timer = null
        }
      }, 1000)
    },
    async resendKey() {
      if (this.resending || this.countdown > 0) return

      if (!this.userEmail) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Email required'),
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: this.$t('Please enter your email to receive the access key.'),
          },
        })
        this.isEditingEmail = true
        return
      }

      this.resending = true
      try {
        await axios.post('/JWT/RequestAccessKey', {
          email: this.userEmail.trim(),
        })

        localStorage.userEmail = this.userEmail.trim()
        this.startCountdown(60)

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Code Sent'),
            icon: 'MailIcon',
            variant: 'success',
            text: this.$t('A new access key has been sent to your email.'),
          },
        })
      } catch (error) {
        const errorMsg = error?.response?.data?.message || this.$t('Failed to resend access key.')
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: errorMsg,
          },
        })
      } finally {
        this.resending = false
      }
    },
    async validationForm() {
      const success = await this.$refs.simpleRules.validate()
      if (!success || this.loading) return

      if (!this.userEmail) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Email required'),
            icon: 'AlertTriangleIcon',
            variant: 'warning',
            text: this.$t('Please enter your email to receive the access key.'),
          },
        })
        this.isEditingEmail = true
        return
      }

      this.loading = true
      try {
        const response = await axios.post('/JWT/LoginAccessKey', {
          email: this.userEmail.trim(),
          accessKey: this.key.trim(),
        })

        const { userData } = response.data
        this.isLoggedIn = true

        useJwt.setToken(response.data.accessToken)
        useJwt.setRefreshToken(response.data.refreshToken)
        localStorage.setItem('userData', JSON.stringify(userData))
        this.$ability.update(userData.ability)

        if (userData.language) {
          this.$i18n.locale = userData.language
        }

        // Verifica permissão do Safe Spot
        try {
          const safeSpotResponse = await this.$store.dispatch('app-safe-spot/fetchIsSafeSpot')
          const isSafeSpot = safeSpotResponse.data
          setSafeSpotPermission(isSafeSpot)
        } catch (error) {
          // Se falhar, manter valor anterior ou não setar
        }

        // Delay para sincronizar localStorage
        await new Promise(resolve => setTimeout(resolve, 100))

        const welcomeName = userData.fullName || userData.username || ''
        const welcomeTitle = welcomeName ? `${this.$t('Welcome')}, ${welcomeName}` : this.$t('Welcome')

        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: welcomeTitle,
            icon: 'CoffeeIcon',
            variant: 'success',
            text: `${this.$t('You have successfully logged in as')} ${userData.role || ''}.`,
          },
        })

        try {
          await this.$router.replace('/')
        } catch (navError) {
          // Erro de navegação não afeta o login
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || this.$t('Invalid access key or email. Please verify and try again.')
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('Authentication Failed'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: errorMessage,
          },
        })
      } finally {
        this.loading = false
      }
    },
  },
  setup() {
    const SYSTEM_APP_STORE_MODULE_NAME = 'app-system'
    const SAFE_SPOT_STORE_MODULE_NAME = 'app-safe-spot'

    // Register modules
    if (!store.hasModule(SYSTEM_APP_STORE_MODULE_NAME)) store.registerModule(SYSTEM_APP_STORE_MODULE_NAME, systemStoreModule)
    if (!store.hasModule(SAFE_SPOT_STORE_MODULE_NAME)) store.registerModule(SAFE_SPOT_STORE_MODULE_NAME, safeSpotStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SYSTEM_APP_STORE_MODULE_NAME)) store.unregisterModule(SYSTEM_APP_STORE_MODULE_NAME)
    })
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.auth-card {
  border-radius: 0.75rem;
  box-shadow: 0 10px 30px 0 rgba(34, 41, 47, 0.1);
  transition: all 0.3s ease;
}

.locale-dropdown-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.25rem;
}

.locale-toggle-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  background-color: #f8f8f8;
  border: 1px solid #ebe9f1;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f0f0;
    border-color: #d8d6de;
  }
}

.brand-logo-img {
  max-height: 48px;
  max-width: 65%;
  object-fit: contain;
}

.access-key-badge {
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
}

.email-chip-container {
  display: flex;
  justify-content: center;
}

.email-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.35rem 0.75rem;
  background-color: rgba(115, 103, 240, 0.08);
  border: 1px solid rgba(115, 103, 240, 0.16);
  border-radius: 20px;
  font-weight: 500;
}

.email-chip .email-text {
  max-width: 220px;
  font-size: 0.85rem;
  color: #5e5873;
}

.email-chip .btn-edit-email {
  display: inline-flex;
  align-items: center;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.email-chip .btn-edit-email:hover {
  opacity: 1;
  color: #7367f0 !important;
}

.access-key-input {
  letter-spacing: 0.55rem;
  font-size: 1.6rem !important;
  font-weight: 700;
  height: 3.5rem;
  text-align: center;
  border-radius: 0.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.access-key-input:focus {
  letter-spacing: 0.55rem;
  border-color: #7367f0;
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
}

.access-key-input::placeholder {
  letter-spacing: 0.55rem;
  color: #b9b9c3;
  font-weight: 400;
}

.btn-submit {
  height: 2.75rem;
  font-size: 1rem;
}

// Dark mode overrides
.dark-layout {
  .auth-card {
    box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.4);
  }

  .locale-toggle-btn {
    background-color: #283046;
    border-color: #3b4253;

    &:hover {
      background-color: #343d55;
    }
  }

  .email-chip {
    background-color: rgba(115, 103, 240, 0.15);
    border-color: rgba(115, 103, 240, 0.25);
  }

  .email-chip .email-text {
    color: #b4b7bd;
  }

  .access-key-input {
    background-color: #283046;
    border-color: #3b4253;
    color: #d0d2d6;
  }

  .access-key-input:focus {
    border-color: #7367f0;
    box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.25);
  }
}
</style>
